"use client";

import { Check, Compass, Lightbulb, Palette, Code as Code2, Rocket } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Prozess() {
  const steps = [{
    phase: "PHASE 01",
    label: "ANALYSE",
    title: "Wir hören zu, bevor wir planen.",
    description: "Jedes Projekt beginnt mit einer tiefen Analyse Ihres Unternehmens. Wir prüfen Ihre Ziele, verstehen Ihre Zielgruppe und identifizieren das Potenzial für Ihren digitalen Auftritt. Keine Annahmen, nur fundierte Strategien.",
    deliverables: ["Stakeholder-Gespräche", "Wettbewerbsanalyse", "Zielgruppen-Definition", "Projekt-Roadmap"],
    Icon: Compass
  }, {
    phase: "PHASE 02",
    label: "KONZEPT",
    title: "Struktur für Ihren Erfolg.",
    description: "Aus Erkenntnissen wird ein konkreter Plan. Wir entwerfen die Informationsarchitektur und das inhaltliche Konzept, das Ihre Besucher überzeugt. Hier trifft strategisches Denken auf kreative Vision.",
    deliverables: ["UX-Konzept", "Inhaltsstrategie", "Strukturplan", "Erfolgsmetriken"],
    Icon: Lightbulb
  }, {
    phase: "PHASE 03",
    label: "UMSETZUNG",
    title: "Design mit Premium-Anspruch.",
    description: "Wir verwandeln Konzepte in visuelle Erlebnisse. Mit einem dezenten goldenen Touch und modernem Design schaffen wir eine Website, die Ihre Marke perfekt repräsentiert und Vertrauen aufbaut.",
    deliverables: ["Visuelles Design", "UI-Entwurf", "Interaktionsdesign", "Prototyping"],
    Icon: Palette
  }, {
    phase: "PHASE 04",
    label: "TECHNIK",
    title: "Performance trifft Präzision.",
    description: "Unsere Entwickler bauen performante, responsive und sichere Websites. Wir achten auf jedes Detail, damit Ihre Seite schnell lädt und auf allen Geräten einwandfrei funktioniert.",
    deliverables: ["Frontend-Entwicklung", "CMS-Integration", "Performance-Optimierung", "Qualitätssicherung"],
    Icon: Code2
  }, {
    phase: "PHASE 05",
    label: "VERÖFFENTLICHUNG",
    title: "Ihr Start in die digitale Zukunft.",
    description: "Der Launch ist erst der Anfang. Wir begleiten Sie bei der Veröffentlichung, richten Analysetools ein und sorgen dafür, dass Ihre neue Website sofort für Ihre Kunden bereitsteht.",
    deliverables: ["Launch-Begleitung", "Analytics-Einrichtung", "SEO-Grundoptimierung", "Wartungs-Support"],
    Icon: Rocket
  }];
  const stats = [{
    label: "DURCHSCHN. DAUER",
    value: "4-8 WOCHEN"
  }, {
    label: "TEAM",
    value: "EXPERTEN-TEAM"
  }, {
    label: "BETREUUNG",
    value: "PERSÖNLICH"
  }];
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: statsRef,
    isVisible: statsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3789" id="der-weg-zu-ihrem-erfolg" className="bg-background text-foreground">
      <div className="py-32 md:py-40 lg:py-[200px] px-6 md:px-10 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div ref={introRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs md:text-sm tracking-[0.25em] uppercase mb-6 text-primary">
              DER PROZESS &mdash; IHR WEG ZUM ZIEL
            </p>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Vom Konzept zum <span className="italic text-primary">Erfolg</span>.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
              Ein strukturierter 4-Schritte-Prozess für maximale Klarheit und Qualität &mdash; von der ersten Analyse bis zur erfolgreichen Veröffentlichung Ihrer Website.
            </p>
          </div>

          <div className="relative mt-20 md:mt-28 lg:mt-32">
            <div aria-hidden="true" className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2 bg-border" />
            <div aria-hidden="true" className="absolute left-6 md:left-1/2 top-0 w-px md:-translate-x-1/2 origin-top bg-primary" style={{
            height: "100%",
            animation: "sparkLineFill 1.6s ease-out forwards",
            transformOrigin: "top"
          }} />

            <style>{`
              @keyframes sparkLineFill {
                from { transform: scaleY(0); }
                to { transform: scaleY(1); }
              }
            `}</style>

            <ol className="space-y-16 md:space-y-24 lg:space-y-28">
              {steps.map((step, i) => {
              const Icon = step.Icon;
              const isEven = i % 2 === 1;
              return <StepItem key={i} data-index={i} step={step} index={i} isEven={isEven} Icon={Icon} />;
            })}
            </ol>
          </div>

          <div ref={statsRef} className={`mt-24 md:mt-32 lg:mt-36 transition-all duration-1000 ease-out ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 border-t border-b border-border">
              {stats.map((stat, i) => <div key={i} data-index={i} className="py-8 md:py-10 px-6 md:px-8 flex flex-col items-center text-center md:border-r last:md:border-r-0 border-b md:border-b-0 border-border">
                  <span className="font-mono text-xs tracking-[0.25em] uppercase mb-3 text-muted-foreground">
                    {stat.label}
                  </span>
                  <span className="font-mono text-lg md:text-xl tracking-wider text-primary">
                    &mdash; {stat.value}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type StepItemProps = {
  step: {
    phase: string;
    label: string;
    title: string;
    description: string;
    deliverables: string[];
  };
  index: number;
  isEven: boolean;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  "data-index"?: number;
};
function StepItem({
  step,
  index,
  isEven,
  Icon,
  ...rest
}: StepItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  const stepNumber = String(index + 1).padStart(2, "0");
  return <li ref={ref} {...rest} className={`relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10 flex items-center justify-center rounded-lg size-12 md:size-14 font-mono text-sm md:text-base font-medium bg-primary text-background shadow-lg" aria-hidden="true">
        {stepNumber}
      </div>

      <div className={`pl-20 md:pl-0 ${isEven ? "md:col-start-2 md:pl-16" : "md:col-start-1 md:pr-16 md:text-right"}`}>
        <p className="font-mono text-xs tracking-[0.25em] uppercase mb-4 text-primary">
          {step.phase} &mdash; {step.label}
        </p>
        <h3 className="font-serif font-default text-2xl md:text-3xl lg:text-4xl leading-[1.15] tracking-tight mb-5">
          {step.title}
        </h3>
        <p className="text-sm font-mono tracking-wider text-primary/70 mb-4 uppercase">
          Detailorientierte Ausführung für nachhaltige Ergebnisse.
        </p>
        <p className="text-base leading-relaxed mb-6 text-muted-foreground">
          {step.description}
        </p>
        <ul className={`space-y-2 ${isEven ? "" : "md:flex md:flex-col md:items-end"}`}>
          {step.deliverables.map((deliverable, di) => <li key={di} data-index={di} className="flex items-center gap-2 font-mono text-xs tracking-wider uppercase">
              {isEven ? <>
                  <Check className="size-3.5 shrink-0 text-primary" strokeWidth={2.5} />
                  <span>{deliverable}</span>
                </> : <>
                  <span className="md:order-2">{deliverable}</span>
                  <Check className="size-3.5 shrink-0 md:order-1 text-primary" strokeWidth={2.5} />
                </>}
            </li>)}
        </ul>
      </div>

      <div className={`hidden md:flex items-center ${isEven ? "md:col-start-1 md:justify-end md:pr-16" : "md:col-start-2 md:justify-start md:pl-16"}`}>
        <div className="relative flex items-center justify-center size-40 lg:size-48 rounded-lg border border-border bg-card">
          <div aria-hidden="true" className="absolute inset-0 rounded-lg bg-gradient-to-br from-primary/10 to-transparent" />
          <Icon className="size-16 lg:size-20 relative text-foreground" strokeWidth={1} />
          <div aria-hidden="true" className="absolute -bottom-2 -right-2 size-3 rounded-lg bg-primary" />
        </div>
      </div>
    </li>;
}